import React, { useState } from 'react';
import { FileDown, Building2, User, Users, ChevronRight, ChevronLeft, Plus, Trash2 } from 'lucide-react';
import ExcelJS from 'exceljs';
import DateInput from './DateInput';
import './AppointmentOrderForm.css';

// Mock data - 2 employees for testing with exact field names matching Form-S.xlsx template
const mockFormData = {
  // Establishment fields
  est_name: "ABC Manufacturing Pvt Ltd",
  est_address: "Plot No. 123, Industrial Area, Phase 2",
  est_city: "Bangalore",
  est_picode: "560001", // Note: template has 'est_picode' (typo)

  // Employer fields
  empl_name: "Mr. Rajesh Kumar (Managing Director)",
  empl_address: "Corporate Office, MG Road",
  empl_address_2: "Bangalore South Division", // Will match {{ empl_address 2 }}
  empl_city: "Bangalore",
  empl_pincode: "560034",

  // Employee array (each employee appears in Row 18, 19, 20, etc.)
  employees: [
    {
      daily_sno: "1",
      daily_empname: "Rajesh Kumar Singh",
      ds: "M", // Gender
      daily_lname: "Singh",
      daily_desig: "Production Manager",
      daily_empno: "EMP001",
      daily_entry_s: "2025-01-15", // Date of entry
      daily_ad: "Adult",
      daily_sh_no: "1", // Shift number
      daily_t_stwork: "09:00 AM", // Time start work
      daily_ri: "1 hour", // Rest interval
      daily_te: "06:00 PM", // Time end
      week_holiday: "Sunday",
      daily_cw: "General", // Category of work
      daily_mxw: "1500", // Maximum wage
      daily_mnw: "800" // Minimum wage
    },
    {
      daily_sno: "2",
      daily_empname: "Priya Sharma",
      ds: "F",
      daily_lname: "Sharma",
      daily_desig: "Quality Inspector",
      daily_empno: "EMP002",
      daily_entry_s: "2024-12-01",
      daily_ad: "Adult",
      daily_sh_no: "1",
      daily_t_stwork: "09:00 AM",
      daily_ri: "1 hour",
      daily_te: "06:00 PM",
      week_holiday: "Sunday",
      daily_cw: "General",
      daily_mxw: "1200",
      daily_mnw: "650"
    }
  ]
};

const FormSUTI = () => {
  const [formData, setFormData] = useState(mockFormData);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Fetch data from API on component mount
  React.useEffect(() => {
    const fetchFormData = async () => {
      setIsLoadingData(true);
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const response = await fetch(`${baseUrl}/api/forms/form_s?establishment_id=1&month=${new Date().getMonth() + 1}&year=${new Date().getFullYear()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });
        if (!response.ok) {
          throw new Error(`API failed with status: ${response.status}`);
        }
        const apiData = await response.json();

        // Expected format: { employees: [{...}, {...}, ...] }
        if (apiData && apiData.employees && Array.isArray(apiData.employees)) {
          setFormData(apiData);
          console.log('Form data loaded from API:', apiData.employees.length, 'employees');
        } else {
          console.warn('API response format unexpected, using mock data');
        }
      } catch (error) {
        console.error('Error fetching form data:', error);
        console.log('Using mock data with 10 employees');
        // Form already initialized with mock data
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchFormData();
  }, []);

  const handleInputChange = (employeeIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      employees: prev.employees.map((emp, idx) =>
        idx === employeeIndex ? { ...emp, [field]: value } : emp
      )
    }));
  };

  const handleEstablishmentChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const nextStep = () => {
    const totalSteps = 1 + formData.employees.length; // 1 (establishment/employer) + employees
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const addEmployee = () => {
    const emptyEmployee = {
      daily_sno: String(formData.employees.length + 1),
      daily_empname: "",
      ds: "M", // Gender
      daily_lname: "",
      daily_desig: "",
      daily_empno: "",
      daily_entry_s: "", // Date of entry
      daily_ad: "Adult",
      daily_sh_no: "1", // Shift number
      daily_t_stwork: "09:00 AM", // Time start work
      daily_ri: "1 hour", // Rest interval
      daily_te: "06:00 PM", // Time end
      week_holiday: "Sunday",
      daily_cw: "General", // Category of work
      daily_mxw: "", // Maximum wage
      daily_mnw: "" // Minimum wage
    };

    setFormData(prev => ({
      ...prev,
      employees: [...prev.employees, emptyEmployee]
    }));
  };

  const removeEmployee = (index) => {
    if (formData.employees.length <= 1) {
      alert('At least one employee is required');
      return;
    }

    const updatedEmployees = formData.employees.filter((_, idx) => idx !== index);
    setFormData(prev => ({ ...prev, employees: updatedEmployees }));

    // If we're on a step beyond the new employee count, go back
    if (currentStep > updatedEmployees.length) {
      setCurrentStep(updatedEmployees.length);
    }
  };

  const generateExcel = async () => {
    setIsGenerating(true);

    try {
      console.log('=== Starting Form S - UTI Generation ===');
      console.log('Loading template...');
      const response = await fetch('/templates/Form-S.xlsx');
      if (!response.ok) {
        throw new Error('Failed to load template');
      }
      const arrayBuffer = await response.arrayBuffer();

      console.log('Creating workbook...');
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        throw new Error('No worksheet found in template');
      }

      console.log(`\n=== Processing ${formData.employees.length} employees ===`);
      console.log('Establishment data:', {
        est_name: formData.est_name,
        est_address: formData.est_address,
        est_city: formData.est_city
      });

      // Remove ALL background fills from entire worksheet
      console.log('\n=== Removing ALL background fills from worksheet ===');
      let fillsRemoved = 0;
      worksheet.eachRow({ includeEmpty: true }, (row) => {
        row.eachCell({ includeEmpty: true }, (cell) => {
          if (cell.fill) {
            cell.fill = null;
            fillsRemoved++;
          }
        });
      });
      console.log(`Removed ${fillsRemoved} cell backgrounds`);

      // First pass: Replace establishment-level placeholders in ALL rows
      console.log('\n=== Pass 1: Replacing establishment/employer fields in entire sheet ===');
      const missingVars = new Set();

      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          const cellValue = cell.value ? String(cell.value) : '';

          if (cellValue.includes('{{')) {
            const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
            let newValue = cellValue;
            let match;
            let hasReplacement = false;

            while ((match = placeholderRegex.exec(cellValue)) !== null) {
              const fieldName = match[1].trim();
              // Normalize field name: replace spaces with underscores for lookup
              const normalizedFieldName = fieldName.replace(/\s+/g, '_');

              // Check organization and employer fields
              if (formData[normalizedFieldName] !== undefined) {
                const replacementValue = formData[normalizedFieldName];
                console.log(`Row ${rowNumber}, Col ${colNumber}: {{ ${fieldName} }} → "${replacementValue}"`);
                newValue = newValue.replace(match[0], replacementValue);
                hasReplacement = true;
              } else {
                // Track missing variables
                missingVars.add(fieldName);
                console.warn(`Row ${rowNumber}, Col ${colNumber}: {{ ${fieldName} }} → ⚠️ NOT FOUND in formData`);
              }
            }

            if (hasReplacement) {
              cell.value = newValue;
            }
          }
        });
      });

      if (missingVars.size > 0) {
        console.warn('\n⚠️ Missing Variables in Pass 1:', Array.from(missingVars));
      }

      // Row 18 is the template row with per-employee placeholders
      const templateRowNumber = 18;
      const templateRow = worksheet.getRow(templateRowNumber);

      // Store template row structure
      console.log('\n=== Pass 2: Duplicating Row 18 for each employee ===');
      const templateCells = [];
      templateRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        templateCells.push({
          colNumber,
          value: cell.value,
          style: {
            font: cell.font,
            alignment: cell.alignment,
            border: cell.border,
            fill: cell.fill,
            numFmt: cell.numFmt
          }
        });
      });

      // Process each employee
      const employeeMissingVars = new Set();

      formData.employees.forEach((employee, index) => {
        console.log(`\n--- Employee ${index + 1}: ${employee.daily_empname} ---`);
        const targetRowNumber = templateRowNumber + index;
        const targetRow = worksheet.getRow(targetRowNumber);

        // Copy template structure to target row
        targetRow.height = templateRow.height;

        templateCells.forEach(({ colNumber, value, style }) => {
          const targetCell = targetRow.getCell(colNumber);

          // Copy cell style (font, alignment, borders, number format)
          targetCell.font = style.font;
          targetCell.alignment = style.alignment;
          targetCell.border = style.border;
          targetCell.numFmt = style.numFmt;

          // Remove ALL background fills from employee rows
          // This ensures no green or colored backgrounds appear in generated rows
          targetCell.fill = null;

          // Replace placeholders with employee data
          if (value && typeof value === 'string') {
            const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
            let cellValue = value;
            let match;

            while ((match = placeholderRegex.exec(value)) !== null) {
              const fieldName = match[1].trim();
              // Normalize field name: replace spaces with underscores for lookup
              const normalizedFieldName = fieldName.replace(/\s+/g, '_');
              const replacementValue = employee[normalizedFieldName] || '';

              if (employee[normalizedFieldName] !== undefined) {
                console.log(`  Col ${colNumber}: {{ ${fieldName} }} → "${replacementValue}"`);
              } else {
                employeeMissingVars.add(fieldName);
                console.warn(`  Col ${colNumber}: {{ ${fieldName} }} → ⚠️ NOT FOUND (using empty string)`);
              }

              cellValue = cellValue.replace(match[0], replacementValue);
            }

            targetCell.value = cellValue;
          } else {
            targetCell.value = value;
          }
        });

        targetRow.commit();
      });

      if (employeeMissingVars.size > 0) {
        console.warn('\n⚠️ Missing Employee Variables in Pass 2:', Array.from(employeeMissingVars));
        console.warn('Please add these fields to the employee objects in mockFormData');
      }

      console.log('\n=== Generating Excel file ===');
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Form-S-UTI-${formData.employees.length}-Employees-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setIsGenerating(false);
      console.log('=== Generation Complete! ===');
      alert(`Form S - UTI generated successfully with ${formData.employees.length} employees!`);
    } catch (error) {
      console.error('Error generating Excel:', error);
      setIsGenerating(false);
      alert(`Error generating Excel: ${error.message}`);
    }
  };

  // Calculate total steps: 1 (organization/employer) + employees
  const totalSteps = 1 + formData.employees.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Render step content
  const renderStepContent = () => {
    // Step 0: Organization & Employer Details
    if (currentStep === 0) {
      return (
        <>
          {/* Info Alert */}
          <div className="info-alert">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <span>
              {isLoadingData
                ? 'Loading employee data from API...'
                : `Employee Declaration Form for Provident Fund. ${formData.employees.length} employee${formData.employees.length !== 1 ? 's' : ''} will be generated.`
              }
            </span>
          </div>

          {/* Organization Details */}
          <div className="form-section">
            <div className="section-header">
              <Building2 size={20} />
              <h3>Organization Details</h3>
            </div>
            <div className="form-grid">
              <div className="form-field full-width">
                <label>Organization Name</label>
                <input
                  type="text"
                  value={formData.est_name || ''}
                  onChange={(e) => handleEstablishmentChange('est_name', e.target.value)}
                  placeholder="Company/Organization Name"
                />
              </div>
              <div className="form-field full-width">
                <label>Organization Address</label>
                <textarea
                  value={formData.est_address || ''}
                  onChange={(e) => handleEstablishmentChange('est_address', e.target.value)}
                  rows={2}
                  placeholder="Complete address"
                />
              </div>
              <div className="form-field">
                <label>City</label>
                <input
                  type="text"
                  value={formData.est_city || ''}
                  onChange={(e) => handleEstablishmentChange('est_city', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Pincode</label>
                <input
                  type="text"
                  value={formData.est_picode || ''}
                  onChange={(e) => handleEstablishmentChange('est_picode', e.target.value)}
                  placeholder="560001"
                />
              </div>
            </div>
          </div>

          {/* Employer Details */}
          <div className="form-section">
            <div className="section-header">
              <User size={20} />
              <h3>Employer Details</h3>
            </div>
            <div className="form-grid">
              <div className="form-field full-width">
                <label>Employer Name</label>
                <input
                  type="text"
                  value={formData.empl_name || ''}
                  onChange={(e) => handleEstablishmentChange('empl_name', e.target.value)}
                  placeholder="Name and designation"
                />
              </div>
              <div className="form-field">
                <label>Employer Address Line 1</label>
                <input
                  type="text"
                  value={formData.empl_address || ''}
                  onChange={(e) => handleEstablishmentChange('empl_address', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Employer Address Line 2</label>
                <input
                  type="text"
                  value={formData.empl_address_2 || ''}
                  onChange={(e) => handleEstablishmentChange('empl_address_2', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>City</label>
                <input
                  type="text"
                  value={formData.empl_city || ''}
                  onChange={(e) => handleEstablishmentChange('empl_city', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Pincode</label>
                <input
                  type="text"
                  value={formData.empl_pincode || ''}
                  onChange={(e) => handleEstablishmentChange('empl_pincode', e.target.value)}
                  placeholder="560034"
                />
              </div>
            </div>
          </div>
        </>
      );
    }

    // Steps 1 to N: Employee details
    if (currentStep > 0 && currentStep <= formData.employees.length) {
      const employeeIndex = currentStep - 1;
      const employee = formData.employees[employeeIndex];

      return (
        <div className="form-section" style={{ background: 'var(--theme-surface)', border: '1px solid #e5e7eb' }}>
          <div className="section-header" style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Users size={20} />
              <h3>Employee {employeeIndex + 1} - UAN Declaration Details</h3>
            </div>
            {formData.employees.length > 1 && (
              <button
                onClick={() => removeEmployee(employeeIndex)}
                style={{
                  background: '#ef4444',
                  color: 'var(--theme-surface, #ffffff)',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <Trash2 size={16} />
                Remove Employee
              </button>
            )}
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Serial No</label>
              <input
                type="text"
                value={employee.daily_sno || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_sno', e.target.value)}
                placeholder="1"
              />
            </div>
            <div className="form-field">
              <label>Employee Name</label>
              <input
                type="text"
                value={employee.daily_empname || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_empname', e.target.value)}
                placeholder="Full name"
              />
            </div>
            <div className="form-field">
              <label>Gender</label>
              <select
                value={employee.ds || 'M'}
                onChange={(e) => handleInputChange(employeeIndex, 'ds', e.target.value)}
              >
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Other</option>
              </select>
            </div>
            <div className="form-field">
              <label>Last Name</label>
              <input
                type="text"
                value={employee.daily_lname || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_lname', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Designation</label>
              <input
                type="text"
                value={employee.daily_desig || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_desig', e.target.value)}
                placeholder="Job title"
              />
            </div>
            <div className="form-field">
              <label>Employee No</label>
              <input
                type="text"
                value={employee.daily_empno || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_empno', e.target.value)}
                placeholder="EMP001"
              />
            </div>
            <div className="form-field">
              <label>Date of Entry</label>
              <DateInput
                value={employee.daily_entry_s || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_entry_s', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Adult/Child</label>
              <select
                value={employee.daily_ad || 'Adult'}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_ad', e.target.value)}
              >
                <option value="Adult">Adult</option>
                <option value="Child">Child</option>
              </select>
            </div>
            <div className="form-field">
              <label>Shift No</label>
              <input
                type="text"
                value={employee.daily_sh_no || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_sh_no', e.target.value)}
                placeholder="1"
              />
            </div>
            <div className="form-field">
              <label>Time Start Work</label>
              <input
                type="text"
                value={employee.daily_t_stwork || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_t_stwork', e.target.value)}
                placeholder="09:00 AM"
              />
            </div>
            <div className="form-field">
              <label>Rest Interval</label>
              <input
                type="text"
                value={employee.daily_ri || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_ri', e.target.value)}
                placeholder="1 hour"
              />
            </div>
            <div className="form-field">
              <label>Time End</label>
              <input
                type="text"
                value={employee.daily_te || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_te', e.target.value)}
                placeholder="06:00 PM"
              />
            </div>
            <div className="form-field">
              <label>Weekly Holiday</label>
              <input
                type="text"
                value={employee.week_holiday || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'week_holiday', e.target.value)}
                placeholder="Sunday"
              />
            </div>
            <div className="form-field">
              <label>Category of Work</label>
              <input
                type="text"
                value={employee.daily_cw || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_cw', e.target.value)}
                placeholder="General"
              />
            </div>
            <div className="form-field">
              <label>Maximum Wage</label>
              <input
                type="text"
                value={employee.daily_mxw || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_mxw', e.target.value)}
                placeholder="1500"
              />
            </div>
            <div className="form-field">
              <label>Minimum Wage</label>
              <input
                type="text"
                value={employee.daily_mnw || ''}
                onChange={(e) => handleInputChange(employeeIndex, 'daily_mnw', e.target.value)}
                placeholder="800"
              />
            </div>
          </div>
          <button
            onClick={() => {
              addEmployee();
              setCurrentStep(formData.employees.length + 1);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: '#10b981',
              color: 'var(--theme-surface, #ffffff)',
              border: 'none',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.95rem',
              cursor: 'pointer',
              marginTop: '1rem'
            }}
          >
            <Plus size={20} />
            Add Another Employee
          </button>
        </div>
      );
    }
  };

  return (
    <div className="appointment-order-container">
      <div className="appointment-header">
        <div>
          <h2 className="appointment-title">Form S - Universal Account Number (UAN)</h2>
          <p className="appointment-subtitle">Employee Declaration Form for Provident Fund</p>
        </div>
      </div>

      <div className="appointment-content">
        {/* Progress Bar */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Step {currentStep + 1} of {totalSteps}
            </span>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {currentStep === 0 ? 'Organization & Employer Info' : `Employee ${currentStep}`}
            </span>
          </div>
          <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="form-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: currentStep === 0 ? '#f8f9fa' : 'var(--theme-surface, #ffffff)',
              color: currentStep === 0 ? '#9ca3af' : '#374151',
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}
          >
            <ChevronLeft size={20} />
            Previous
          </button>

          {currentStep === totalSteps - 1 ? (
            <button
              onClick={generateExcel}
              disabled={isGenerating}
              className={`btn-generate ${isGenerating ? 'generating' : ''}`}
            >
              {isGenerating ? (
                <>
                  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 0 20" opacity="0.75"/>
                  </svg>
                  <span>Generating Excel...</span>
                </>
              ) : (
                <>
                  <FileDown size={20} />
                  <span>Generate Form</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={nextStep}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                border: 'none',
                background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
                color: 'var(--theme-surface, #ffffff)',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500'
              }}
            >
              Next
              <ChevronRight size={20} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormSUTI;
